/**
 * DateTimePicker Component
 * Combines DatePicker and TimePicker for full datetime selection
 */

import * as React from 'react';
import { CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';

interface DateTimePickerProps {
  value?: string | null;
  onChange: (datetime: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  required?: boolean;
}

/**
 * Converts ISO datetime string to separate date and time
 * @param datetime ISO string like "2025-10-29T14:30:00.000Z"
 * @returns { date: "2025-10-29", time: "14:30" }
 */
function parseDateTime(datetime: string | null | undefined) {
  if (!datetime) return { date: null, time: null };

  try {
    const dt = new Date(datetime);
    const date = dt.toISOString().split('T')[0]; // yyyy-MM-dd
    const time = dt.toTimeString().slice(0, 5); // HH:mm
    return { date, time };
  } catch {
    return { date: null, time: null };
  }
}

/**
 * Combines date and time into ISO datetime string
 * @param date "2025-10-29"
 * @param time "14:30"
 * @returns "2025-10-29T14:30:00.000Z" or null
 */
function combineDateTime(date: string | null, time: string | null): string | null {
  if (!date) return null;

  // If no time provided, use 00:00 (midnight)
  const timeStr = time || '00:00';

  try {
    const datetime = new Date(`${date}T${timeStr}:00`);
    return datetime.toISOString();
  } catch {
    return null;
  }
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Seleccionar fecha y hora',
  disabled = false,
  id,
  label,
  required = false,
}: DateTimePickerProps) {
  const { date, time } = parseDateTime(value);

  const handleDateChange = (newDate: string | null) => {
    const combined = combineDateTime(newDate, time);
    onChange(combined);
  };

  const handleTimeChange = (newTime: string | null) => {
    const combined = combineDateTime(date, newTime);
    onChange(combined);
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id} className="flex items-center gap-1">
          <CalendarClock className="h-4 w-4" />
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <div className="grid grid-cols-2 gap-2">
        <DatePicker
          value={date}
          onChange={handleDateChange}
          placeholder="Fecha"
          disabled={disabled}
          id={id ? `${id}-date` : undefined}
        />
        <TimePicker
          value={time}
          onChange={handleTimeChange}
          placeholder="Hora"
          disabled={disabled}
          id={id ? `${id}-time` : undefined}
        />
      </div>
      {!date && !time && (
        <p className="text-xs text-muted-foreground">{placeholder}</p>
      )}
    </div>
  );
}
